import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiRequest, getAuthToken, setAuthToken } from "@client/api/client";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Staff Sign In | African Karate Federation" },
      {
        name: "description",
        content:
          "Secure sign in for admins and instructors of the karate referee examination platform.",
      },
      {
        property: "og:title",
        content: "Staff Sign In | African Karate Federation",
      },
      {
        property: "og:description",
        content: "Admin and instructor access to the examination platform.",
      },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getAuthToken()) {
      navigate({ to: "/dashboard", replace: true });
    }
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 1) {
      toast.error("Password is required.");
      return;
    }
    setLoading(true);
    try {
      const response = await apiRequest<{
        token: string;
        user: any;
        mustChangePassword?: boolean;
      }>("/api/auth/login", {
        method: "POST",
        public: true,
        body: { email, password },
      });
      setAuthToken(response.token);
      // Ensure router context updates and invalidates queries if needed
      window.dispatchEvent(new Event("auth-changed"));

      if (response.mustChangePassword) {
        navigate({ to: "/change-password", replace: true });
      } else {
        navigate({ to: "/dashboard", replace: true });
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Authentication failed.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-primary px-4 py-12">
      <div className="w-full max-w-md rounded-lg bg-card p-8 shadow-lg">
        <div className="flex flex-col items-center justify-center space-y-2">
          <div className="mb-4 flex h-48 w-48 sm:h-52 sm:w-52 items-center justify-center rounded-3xl bg-white p-5 shadow-2xl border border-border/20 ring-8 ring-black/5 dark:ring-white/10">
            <img
              src="/UFAK_Karate_Africa_Vertical.svg"
              alt="African Karate Federation Logo"
              className="h-full w-full object-contain drop-shadow-sm"
            />
          </div>
          <p className="hall-heading text-base font-semibold text-foreground text-center">
            African Karate Federation
            <br />
            Referees Committee
          </p>
          <h1 className="hall-heading text-2xl font-bold mt-2">Staff Portal</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Admin and instructor accounts only. Referees do not sign in.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Please wait…" : "Sign in"}
          </Button>
        </form>

        <p className="mt-6 text-xs text-muted-foreground">
          If you need an instructor account, please contact your Administrator.
        </p>
        <p className="mt-2 text-xs text-center text-muted-foreground">
          <Link to="/recovery" className="hover:underline text-primary">
            Forgot your password?
          </Link>
        </p>
      </div>
      <p className="absolute bottom-4 left-0 right-0 text-center text-xs text-primary-foreground/70">
        Powered by Ptahelix Intelligent Solutions
      </p>
    </div>
  );
}
